import {AttrsSchema, Component, jsx} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';
import round from 'lodash/round';
import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {commaizeNumber, formatMetricNumber, formatPercent} from 'common/util';
import {NUMERIC_DATA_PRECISION} from 'common/report/insights/util';
import {ChartTooltipEl} from 'common/components/chart-tooltip';
import {TOOLTIP_THROTTLE_TIME} from 'common/components/chart-tooltip/util';

import css from './index.inline.styl';
import {MetricChartEl, MetricChartSize} from './metric';
import {
  MetricChartClick,
  MetricFormatter,
  MetricFormatType,
  MetricSegment,
  MultiMetricChartAttributes,
  MultiMetricChartState,
  TooltipDataCallback,
} from './types';
import {getGridDimensions} from './utils';

const NUM_CENTERED_METRICS = 6;

export class MultiMetricChart extends Component<MultiMetricChartState, MultiMetricChartAttributes> {
  private selfResizeObserver: ResizeObserver;
  private _metricFormatter: MetricFormatter = MetricFormatType.Number;
  private _tooltipDataCallback: TooltipDataCallback;
  private _onMetricChartClick: MetricChartClick;
  private hideTooltipTimeout: number;

  static get attrsSchema(): AttrsSchema<MultiMetricChartAttributes> {
    return {
      ...super.attrsSchema,
      metrics: {type: `json`, tsType: `MetricSegment[]`},
      'lift-type': `string`,
      'selected-metric': {type: `json`, tsType: `MetricSegment`},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        selfWidth: 0,
        selfHeight: 0,
        hoverTooltip: null,
      },
    };
  }

  set metricFormatter(formatter: MetricFormatter) {
    this._metricFormatter = formatter;
    this.update();
  }

  get metricFormatter() {
    return this._metricFormatter;
  }

  get tooltipDataCallback() {
    return this._tooltipDataCallback;
  }

  set tooltipDataCallback(f: TooltipDataCallback) {
    this._tooltipDataCallback = f;
  }

  get onMetricChartClick() {
    return this._onMetricChartClick;
  }

  set onMetricChartClick(f: MetricChartClick) {
    this._onMetricChartClick = f;
  }

  onMetricMouseMove(ev: MouseEvent, metricSegment: MetricSegment) {
    ev.stopPropagation();

    const {clientX, clientY} = ev;
    this.clearHideTooltipTimeout();

    if (!this.tooltipDataCallback || this.attr(`selected-metric`)) {
      return;
    }

    const tooltipAttrs = this.tooltipDataCallback(metricSegment);

    this.update({
      hoverTooltip: {
        attrs: tooltipAttrs,
        props: {positioning: {clientX, clientY}},
      },
    });
  }

  setHideTooltipTimeout() {
    this.clearHideTooltipTimeout();
    this.hideTooltipTimeout = window.setTimeout(() => this.update({hoverTooltip: null}), 250);
  }

  clearHideTooltipTimeout() {
    window.clearTimeout(this.hideTooltipTimeout);
    this.hideTooltipTimeout = null;
  }

  formatMetric(value: number): string {
    if (this._metricFormatter === MetricFormatType.Number) {
      return formatMetricNumber(value);
    } else if (this._metricFormatter === MetricFormatType.Percent) {
      return formatPercent(value);
    } else {
      return this._metricFormatter(value);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {width, height} = entries[0].contentRect;
      this.update({selfWidth: width, selfHeight: height});
    });

    this.selfResizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.selfResizeObserver.disconnect();
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected-metric` && this.attr(`selected-metric`)) {
      this.update({hoverTooltip: null});
    }
  }

  getMetricSize() {
    // adjust as needed for different metric sizes
    const minHeightForLargeSize = 200;
    const minWidthForLargeSize = 256;

    const {rows, columns} = this.getGridDimensions();
    return this.state.selfHeight / rows >= minHeightForLargeSize &&
      this.state.selfWidth / columns >= minWidthForLargeSize
      ? MetricChartSize.Large
      : MetricChartSize.Small;
  }

  getGridDimensions() {
    return getGridDimensions(this.state.selfWidth, this.state.selfHeight, this.attr(`metrics`).length);
  }

  getMetricsToDisplay(): {gridMetrics: MetricSegment[]; bottomMetrics: MetricSegment[]} {
    const metrics = this.attr(`metrics`);
    const {rows, columns} = this.getGridDimensions();

    // only show as many metrics as allowed by the grid
    const maxAllowed = rows * columns;
    const truncatedMetrics = metrics.slice(0, maxAllowed);

    // render bottom row as separate grid in cases that there is an odd number of metrics
    // this allows us to fill up the remaining space
    const numBottomMetrics =
      maxAllowed === truncatedMetrics.length ? columns : truncatedMetrics.length - columns * (rows - 1);
    const [gridMetrics, bottomMetrics] = [
      truncatedMetrics.slice(0, truncatedMetrics.length - numBottomMetrics),
      truncatedMetrics.slice(truncatedMetrics.length - numBottomMetrics),
    ];

    return {gridMetrics, bottomMetrics};
  }

  renderTemplate() {
    const metrics = this.attr(`metrics`);
    if (!metrics.length) {
      return <div sel='.root' />;
    }

    const {rows, columns} = this.getGridDimensions();

    const {gridMetrics, bottomMetrics} = this.getMetricsToDisplay();
    const metricSize = this.getMetricSize();
    const isCentered = metrics.length <= NUM_CENTERED_METRICS;

    const renderMetricChart = (
      {comparisonValue, label, value, path, stepIndex, subLabel, color, liftComparisonValue}: MetricSegment,
      idx,
      numMetrics,
    ) => {
      const formatted = this.formatMetric(value);
      let valueTooltip =
        this.metricFormatter === MetricFormatType.Number ? commaizeNumber(round(value, NUMERIC_DATA_PRECISION)) : ``;

      let liftFormattedCompare;

      if (this.attr(`lift-type`) && isNumber(comparisonValue)) {
        if (this.metricFormatter === MetricFormatType.Percent) {
          valueTooltip = this.formatMetric(round(value, NUMERIC_DATA_PRECISION));
        }

        // lift CTP and Baseline have extra numerical values to display
        valueTooltip += ` / ${this.formatMetric(comparisonValue)}`;
        liftFormattedCompare = this.formatMetric(comparisonValue);
      }

      const isLastInRow = (idx + 1) % columns === numMetrics % columns;
      const segment = {
        value,
        label,
        path,
        color,
        comparisonValue,
        ...{stepIndex},
      } as MetricSegment;
      const enableClick = !!this.onMetricChartClick;
      return (
        <div
          sel='.metric-container'
          attrs={{
            elref: `metricContainer`,
            ...(enableClick && {tabindex: 0}),
          }}
          class={{
            'first-in-row': idx === 0,
            'last-in-row': isLastInRow,
            selected: this.attr(`selected-metric`)?.label === label,
            clickable: enableClick,
          }}
          on={{
            ...(!!this.tooltipDataCallback && {
              mousemove: throttle((ev) => this.onMetricMouseMove(ev, segment), TOOLTIP_THROTTLE_TIME, {
                leading: true,
                trailing: false,
              }),
              mouseleave: () => this.setHideTooltipTimeout(),
            }),
            ...(enableClick && {click: (ev) => this.onMetricChartClick(ev, segment)}),
          }}
        >
          <MetricChartEl
            attrs={{
              elref: `metric`,
              'display-value': formatted,
              'value-tooltip': valueTooltip,
              'comparison-value': comparisonValue,
              'sub-label': subLabel,
              'is-centered': isCentered,
              'hide-tooltip': !!this.tooltipDataCallback,
              size: metricSize,
              label,
              value,
              color,
              ...(this.attr(`lift-type`)
                ? {
                    'lift-context': {
                      liftType: this.attr(`lift-type`),
                      liftComparisonValue,
                      comparisonValue: liftFormattedCompare,
                    },
                  }
                : {}),
            }}
          />
        </div>
      );
    };

    return (
      <div sel='.root'>
        <div
          sel='.grid'
          style={{
            'grid-template-columns': `repeat(${columns}, 1fr)`,
            'grid-template-rows': `repeat(${rows}, 1fr)`,
          }}
        >
          {gridMetrics.map((metric, idx) => renderMetricChart(metric, idx, gridMetrics.length))}
          {bottomMetrics.length ? (
            <div
              sel='.bottom-row'
              attrs={{elref: `bottomRow`}}
              style={{'grid-template-columns': `repeat(${bottomMetrics.length}, 1fr)`}}
            >
              {bottomMetrics.map((metric, idx) => renderMetricChart(metric, idx, bottomMetrics.length))}
            </div>
          ) : null}
        </div>
        {this.state.hoverTooltip && (
          <ChartTooltipEl attrs={this.state.hoverTooltip.attrs} props={this.state.hoverTooltip.props} />
        )}
      </div>
    );
  }
}

interface MultiMetricChartJsxProps {
  attrs: MultiMetricChartAttributes;
  props?: {
    metricFormatter?: MetricFormatter;
    onMetricChartClick?: (ev: MouseEvent, segment: MetricSegment) => void;
    tooltipDataCallback?: TooltipDataCallback;
  };
}

export const MultiMetricChartEl = defineAndMakeJsxFactory<MultiMetricChartJsxProps>(
  `mp-multi-metric`,
  MultiMetricChart,
);
export const MultiMetricChartReact = panel2react<MultiMetricChartJsxProps>(`mp-multi-metric`, MultiMetricChart);
